import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { CopyableAddress, CopyableMint } from '../common/CopyButton';
import '../../styles/compact-automated-bot.css';

const CompactAutomatedBot = ({ wallets, activeWallet, onBotStart, setCurrentView }) => {
  const { tokens } = useAuth();
  const { showSuccess, showError } = useToast();

  const [botConfig, setBotConfig] = useState({
    tokenMint: '',
    initialAmount: '',
    walletId: activeWallet?.id || '',
    stopLossPercentage: 10,
    trailingStopEnabled: true
  });

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('config'); // config, confirm, starting

  // Enhanced features state
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenInfoLoading, setTokenInfoLoading] = useState(false);
  const [expectedTokens, setExpectedTokens] = useState(null);
  const [tradingFees, setTradingFees] = useState(null);
  const [walletBalance, setWalletBalance] = useState({ sol: 0 });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepump.urion.live/api';

  // Debounce ref for token fetching
  const tokenFetchTimeoutRef = useRef(null);

  // Authenticated request helper
  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('Please log in to use trading features');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Validate Solana address format
  const isValidSolanaAddress = (address) => {
    if (!address || address.length !== 44) return false;
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    return base58Regex.test(address);
  };

  // Fetch token information
  const fetchTokenInfo = useCallback(async (mintAddress) => {
    if (!mintAddress || !isValidSolanaAddress(mintAddress)) {
      return;
    }

    setTokenInfoLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/token-info/${mintAddress}`);
      const data = await response.json();

      if (data.success) {
        setTokenInfo(data.data.token);
      } else {
        setTokenInfo(null);
      }
    } catch (error) {
      console.error('Error fetching token info:', error);
      setTokenInfo(null);
    } finally {
      setTokenInfoLoading(false);
    }
  }, [API_BASE_URL]);

  // Debounced version of fetchTokenInfo
  const debouncedFetchTokenInfo = useCallback((mintAddress) => {
    if (tokenFetchTimeoutRef.current) {
      clearTimeout(tokenFetchTimeoutRef.current);
    }

    tokenFetchTimeoutRef.current = setTimeout(() => {
      fetchTokenInfo(mintAddress);
    }, 500); // 500ms debounce
  }, [fetchTokenInfo]);

  // Calculate expected tokens
  const calculateExpectedTokens = useCallback(async (mintAddress, solAmount) => {
    if (!mintAddress || !solAmount || solAmount <= 0) {
      setExpectedTokens(null);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/token-info/calculate-amount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tokenMint: mintAddress,
          solAmount: parseFloat(solAmount),
          direction: 'sol-to-token'
        })
      });

      const data = await response.json();

      if (data.success) {
        setExpectedTokens(data.data.calculation);
      } else {
        setExpectedTokens(null);
      }
    } catch (error) {
      console.error('Error calculating expected tokens:', error);
      setExpectedTokens(null);
    }
  }, [API_BASE_URL]);

  // Calculate trading fees
  const calculateTradingFees = useCallback(async (tokenMint, solAmount) => {
    if (!tokenMint || !solAmount) return;

    try {
      const SOL_MINT = 'So11111111111111111111111111111111111111112';
      const response = await fetch(`${API_BASE_URL}/token-info/trading-fees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          inputMint: SOL_MINT,
          outputMint: tokenMint,
          amount: parseFloat(solAmount)
        })
      });

      const data = await response.json();

      if (data.success) {
        setTradingFees(data.data.fees);
      }
    } catch (error) {
      console.error('Error calculating fees:', error);
    }
  }, [API_BASE_URL]);

  // Effects
  useEffect(() => {
    if (botConfig.tokenMint) {
      debouncedFetchTokenInfo(botConfig.tokenMint);
    } else {
      setTokenInfo(null);
      setExpectedTokens(null);
      setTradingFees(null);
    }
  }, [botConfig.tokenMint, debouncedFetchTokenInfo]);

  useEffect(() => {
    if (botConfig.tokenMint && botConfig.initialAmount) {
      calculateExpectedTokens(botConfig.tokenMint, botConfig.initialAmount);
      calculateTradingFees(botConfig.tokenMint, botConfig.initialAmount);
    } else {
      setExpectedTokens(null);
      setTradingFees(null);
    }
  }, [botConfig.tokenMint, botConfig.initialAmount, calculateExpectedTokens, calculateTradingFees]);

  // Fetch wallet balance for bot
  const fetchBotWalletBalance = useCallback(async () => {
    if (!activeWallet) return;

    try {
      const response = await makeAuthenticatedRequest(`/wallets/${activeWallet.id}/balance`);
      if (response.success) {
        setWalletBalance({
          sol: response.data.balance.sol || 0
        });
      }
    } catch (error) {
      console.warn('Could not fetch wallet balance:', error.message);
    }
  }, [activeWallet, makeAuthenticatedRequest]);

  // Set percentage amount for bot based on SOL balance
  const setBotPercentageAmount = (percentage) => {
    if (!activeWallet) {
      showError('Please connect a wallet first');
      return;
    }

    // For bot, always use percentage of SOL balance (minus some for fees)
    const maxAmount = Math.max(0, walletBalance.sol - 0.02); // Reserve 0.02 SOL for fees
    const amount = (maxAmount * percentage / 100).toFixed(3);
    setBotConfig({ ...botConfig, initialAmount: amount });
  };

  // Fetch wallet balance when wallet changes
  useEffect(() => {
    fetchBotWalletBalance();
  }, [fetchBotWalletBalance]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tokenFetchTimeoutRef.current) {
        clearTimeout(tokenFetchTimeoutRef.current);
      }
    };
  }, []);

  const validateConfig = () => {
    if (!botConfig.tokenMint) {
      showError('Please enter a token address');
      return false;
    }
    if (!botConfig.initialAmount || parseFloat(botConfig.initialAmount) < 0.005) {
      showError('Minimum initial amount is 0.005 SOL');
      return false;
    }
    if (!botConfig.walletId) {
      showError('Please select a wallet');
      return false;
    }
    return true;
  };

  const proceedToConfirm = () => {
    if (validateConfig()) {
      setStep('confirm');
    }
  };

  const startBot = async () => {
    try {
      setLoading(true);
      setStep('starting');

      const data = await makeAuthenticatedRequest('/trading/start-bot', {
        method: 'POST',
        body: JSON.stringify({
          tokenMint: botConfig.tokenMint,
          walletId: parseInt(botConfig.walletId),
          initialAmount: parseFloat(botConfig.initialAmount),
          stopLossPercentage: parseFloat(botConfig.stopLossPercentage),
          trailingStopEnabled: botConfig.trailingStopEnabled
        })
      });

      // Reset form
      setBotConfig({
        tokenMint: '',
        initialAmount: '',
        walletId: activeWallet?.id || '',
        stopLossThreshold: 10
      });
      setStep('config');

      if (onBotStart) onBotStart();

      showSuccess(`Trading bot started! Session ID: ${data.data.session.sessionId}`);
    } catch (error) {
      showError(`Failed to start bot: ${error.message}`);
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep('config');
  };

  const platformWallets = wallets.filter(w => w.wallet_type === 'platform');

  // Show login prompt if not authenticated
  if (!tokens?.access_token) {
    return (
      <div className="compact-automated-bot">
        <div className="auth-required">
          <div className="auth-icon">🔐</div>
          <h3>Authentication Required</h3>
          <p>Please log in to use the automated trading features.</p>
          <div className="auth-actions">
            <button className="login-btn" onClick={() => window.location.href = '/login'}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compact-automated-bot">
      {step === 'config' && (
        <div className="bot-config-form">
          <div className="form-header" style={{ background: 'black' }}>
            <h4>🤖 Automated Trading Bot</h4>
            <p>Set up intelligent trading with stop-loss protection</p>
          </div>

          {/* Token Input */}
          <div className="input-group">
            <label className="input-label">Token Address</label>
            <div className="token-input-wrapper">
              <input
                type="text"
                className="compact-input"
                value={botConfig.tokenMint}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setBotConfig({ ...botConfig, tokenMint: newValue });
                  // Trigger debounced fetch immediately on change
                  if (newValue) {
                    debouncedFetchTokenInfo(newValue);
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault(); // Prevent default paste behavior
                  const pastedValue = e.clipboardData.getData('text').trim();
                  if (pastedValue) {
                    setBotConfig({ ...botConfig, tokenMint: pastedValue });
                    // Trigger fetch immediately for paste
                    if (isValidSolanaAddress(pastedValue)) {
                      fetchTokenInfo(pastedValue);
                    }
                  }
                }}
                placeholder="Enter token mint address..."
                disabled={loading}
              />
              {tokenInfoLoading && (
                <div className="token-loading">
                  <div className="loading-spinner"></div>
                </div>
              )}
            </div>

            {/* Token Information Display */}
            {tokenInfo && (
              <div className="token-info-display">
                <div className="token-header">
                  <div className="token-identity">
                    {tokenInfo.logoURI ? (
                      <img
                        src={tokenInfo.logoURI}
                        alt={tokenInfo.symbol}
                        className="token-logo"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    {!tokenInfo.logoURI && (
                      <div className="token-logo-placeholder">
                        {tokenInfo.symbol?.charAt(0) || '?'}
                      </div>
                    )}
                    <div className="token-names">
                      <span className="token-symbol">{tokenInfo.symbol}</span>
                      <span className="token-name">{tokenInfo.name}</span>
                    </div>
                  </div>
                  {tokenInfo.isVerified && (
                    <div className="verified-badge">✓</div>
                  )}
                </div>

                <div className="token-metrics">
                  <div className="metric">
                    <span className="metric-label">Price</span>
                    <span className="metric-value">
                      ${tokenInfo.priceUSD ? (tokenInfo.priceUSD < 0.001 ? tokenInfo.priceUSD.toExponential(3) : tokenInfo.priceUSD.toFixed(6)) : 'N/A'}
                    </span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Market Cap</span>
                    <span className="metric-value">
                      ${tokenInfo.marketCap ? (tokenInfo.marketCap / 1000000).toFixed(2) + 'M' : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Initial Amount */}
          <div className="input-group">
            <label className="input-label">Initial SOL Amount</label>
            <div className="amount-input-wrapper">
              <input
                type="number"
                className="compact-input"
                value={botConfig.initialAmount}
                onChange={(e) => setBotConfig({ ...botConfig, initialAmount: e.target.value })}
                placeholder="0.1"
                step="0.01"
                min="0.005"
                disabled={loading}
              />
              <span className="amount-suffix">SOL</span>
            </div>

            {/* Percentage Buttons for Bot */}
            <div className="percentage-buttons">
              <button
                className="percentage-btn"
                onClick={() => setBotPercentageAmount(25)}
                disabled={loading}
              >
                25%
              </button>
              <button
                className="percentage-btn"
                onClick={() => setBotPercentageAmount(50)}
                disabled={loading}
              >
                50%
              </button>
              <button
                className="percentage-btn"
                onClick={() => setBotPercentageAmount(75)}
                disabled={loading}
              >
                75%
              </button>
              <button
                className="percentage-btn max"
                onClick={() => setBotPercentageAmount(100)}
                disabled={loading}
              >
                MAX
              </button>
            </div>

            <div className="input-help">Minimum: 0.005 SOL</div>

            {/* Expected Tokens Display */}
            {expectedTokens && tokenInfo && (
              <div className="expected-tokens">
                <div className="expected-result">
                  <span className="expected-label">Expected tokens:</span>
                  <span className="expected-value">
                    {expectedTokens.tokensReceived?.toFixed(6) || '0'} {tokenInfo.symbol}
                  </span>
                </div>
                <div className="expected-details">
                  <span className="detail-item">
                    Rate: {expectedTokens.exchangeRate?.toFixed(2) || '0'} {tokenInfo.symbol}/SOL
                  </span>
                  <span className="detail-item">
                    Impact: {expectedTokens.priceImpact?.toFixed(2) || '0'}%
                  </span>
                </div>
              </div>
            )}

            {/* Trading Fees for Bot */}
            {tradingFees && (
              <div className="bot-trading-fees">
                <div className="fees-summary">
                  <span className="fees-label">Est. Trading Fees:</span>
                  <span className="fees-value">{tradingFees.totalFee?.toFixed(6) || '0'} SOL</span>
                </div>
              </div>
            )}
          </div>

          {/* Stop-Loss Configuration */}
          <div className="input-group">
            <label className="input-label">Stop-Loss Settings</label>

            <div className="stop-loss-config">
              <div className="stop-loss-percentage">
                <label className="sub-label">Stop-Loss Percentage</label>
                <div className="percentage-input-wrapper">
                  <input
                    type="number"
                    className="compact-input percentage-input"
                    value={botConfig.stopLossPercentage}
                    onChange={(e) => setBotConfig({ ...botConfig, stopLossPercentage: parseFloat(e.target.value) || 10 })}
                    min="1"
                    max="50"
                    step="1"
                    disabled={loading}
                  />
                  <span className="percentage-suffix">%</span>
                </div>
                <div className="input-help">Sell when price drops by this percentage from highest point</div>
              </div>

              <div className="trailing-stop-toggle">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={botConfig.trailingStopEnabled}
                    onChange={(e) => setBotConfig({ ...botConfig, trailingStopEnabled: e.target.checked })}
                    disabled={loading}
                  />
                  <span className="checkbox-text">Enable Trailing Stop-Loss</span>
                </label>
                <div className="input-help">
                  {botConfig.trailingStopEnabled
                    ? "Stop-loss follows price up, protecting profits"
                    : "Stop-loss stays at initial purchase price level"
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Wallet Selection */}
          <div className="input-group">
            <label className="input-label">Trading Wallet</label>
            <select
              className="compact-select"
              value={botConfig.walletId}
              onChange={(e) => setBotConfig({ ...botConfig, walletId: e.target.value })}
              disabled={loading}
            >
              <option value="">Select wallet...</option>
              {platformWallets.map(wallet => (
                <option key={wallet.id} value={wallet.id}>
                  {wallet.name || `Wallet ${wallet.id}`} ({wallet.wallet_address.slice(0, 8)}...)
                </option>
              ))}
            </select>
          </div>

          {/* Bot Features Preview */}
          <div className="bot-features-preview">
            <div className="feature-item">
              <span className="feature-icon">🛡️</span>
              <div className="feature-text">
                <strong>Trailing Stop-Loss</strong>
                <p>Sell at {botConfig.stopLossPercentage}% drop from highest price</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📈</span>
              <div className="feature-text">
                <strong>Profit Protection</strong>
                <p>Stop-loss follows price up to lock in gains</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🔄</span>
              <div className="feature-text">
                <strong>Smart Re-entry</strong>
                <p>Buy back when price recovers</p>
              </div>
            </div>
          </div>

          <button
            className="proceed-btn"
            onClick={proceedToConfirm}
            disabled={loading}
          >
            Review Configuration
          </button>
        </div>
      )}

      {step === 'confirm' && (
        <div className="bot-confirmation">
          <div className="confirm-header">
            <h4>Confirm Bot Configuration</h4>
            <button className="back-btn" onClick={resetForm}>←</button>
          </div>

          <div className="config-summary">
            <div className="summary-item">
              <span className="label">Token:</span>
              <div className="value">
                <CopyableMint
                  mintAddress={botConfig.tokenMint}
                  truncate={true}
                  truncateLength={8}
                />
              </div>
            </div>
            <div className="summary-item">
              <span className="label">Initial Amount:</span>
              <span className="value">{botConfig.initialAmount} SOL</span>
            </div>
            <div className="summary-item">
              <span className="label">Wallet:</span>
              <div className="value">
                <CopyableAddress
                  address={platformWallets.find(w => w.id.toString() === botConfig.walletId)?.wallet_address || ''}
                  truncate={true}
                  truncateLength={8}
                />
              </div>
            </div>
            <div className="summary-item">
              <span className="label">Stop-Loss:</span>
              <span className="value">{botConfig.stopLossPercentage}% {botConfig.trailingStopEnabled ? '(Trailing)' : '(Fixed)'}</span>
            </div>
          </div>

          <div className="risk-warning">
            <div className="warning-icon">⚠️</div>
            <div className="warning-text">
              <strong style={{ color: 'tomato' }}>Risk Warning</strong>
              <p style={{ color: 'tomato' }}>Automated trading involves risk. The bot will execute trades automatically based on market conditions.</p>
            </div>
          </div>

          <button
            className="start-bot-btn"
            onClick={startBot}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="loading-spinner"></div>
                Starting Bot...
              </>
            ) : (
              '🚀 Start Trading Bot'
            )}
          </button>
        </div>
      )}

      {step === 'starting' && (
        <div className="bot-starting">
          <div className="starting-animation">
            <div className="pulse-circle"></div>
            <div className="starting-icon">🤖</div>
          </div>
          <h4>Starting Trading Bot...</h4>
          <p>Initializing automated trading system</p>
          <div className="progress-steps">
            <div className="step active">✓ Validating configuration</div>
            <div className="step active">✓ Connecting to wallet</div>
            <div className="step active">⏳ Starting trading session</div>
          </div>
        </div>
      )}

      {platformWallets.length === 0 && (
        <div className="no-wallets-warning">
          <div className="warning-icon">💼</div>
          <h4>No Platform Wallets Found</h4>
          <p>You need a platform wallet to use the automated trading bot.</p>
          <button className="create-wallet-btn" onClick={() => setCurrentView('wallets')}>
            Create Platform Wallet
          </button>
        </div>
      )}
    </div>
  );
};

export default CompactAutomatedBot;